/**
 * FactoidWidget — rendered inline in chat for fact-check responses.
 *
 * Displays a compact card with the question and a concise answer. A button
 * opens the full detailed answer (with evidence, limitations, sources) in
 * the right-side detail panel. Uses the same ReportPanelContext as
 * ReportWidget so only one panel is open at a time.
 */

"use client"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { HelpCircle, ExternalLink } from "lucide-react"
import { MarkdownRenderer } from "../MarkdownRenderer"
import { useReportPanel } from "../ReportPanelContext"

interface FactoidWidgetProps {
  /** The research question being answered. */
  question: string
  /** The evidence-based answer (markdown). */
  answer: string
  /** Known limitations of the evidence (markdown). */
  limitations: string
  /** Source citations and references (markdown). */
  sources: string
}

/**
 * Renders a compact fact-check card with a button to open the full answer
 * in the side panel.
 *
 * @param question - The research question.
 * @param answer - The answer with evidence.
 * @param limitations - Limitations of the evidence.
 * @param sources - Source citations.
 * @returns A Card component with question, short answer, and "Open full answer" button.
 */
export function FactoidWidget({ question, answer, limitations, sources }: FactoidWidgetProps) {
  const { openReport } = useReportPanel()

  // Use first 1-2 sentences of the answer as a summary
  const answerSummary = answer.split("\n")[0] || answer

  return (
    <Card className="my-3 border-primary/20 shadow-md max-w-3xl">
      <CardHeader className="pb-2 pt-4 px-5">
        <div className="flex items-start gap-2">
          <HelpCircle className="h-5 w-5 text-primary mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base font-semibold leading-tight">
              Fact-Check
            </CardTitle>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-5 pb-4 pt-0 space-y-3">
        {/* Question */}
        <div className="border-l-2 border-muted-foreground/30 pl-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
            Question
          </p>
          <div className="text-sm text-foreground prose prose-sm max-w-none">
            <MarkdownRenderer content={question} />
          </div>
        </div>

        {/* Answer Preview */}
        <div className="border-l-2 border-primary/40 pl-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
            Answer
          </p>
          <div className="text-sm text-foreground prose prose-sm max-w-none line-clamp-3">
            <MarkdownRenderer content={answerSummary} />
          </div>
        </div>

        {/* Open in side panel button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => openReport({
            kind: "factoid",
            title: question,
            summary: answerSummary,
            markdown: "",
            sections: [
              { label: "Question", content: question },
              { label: "Answer", content: answer },
              { label: "Limitations", content: limitations },
              { label: "Sources", content: sources },
            ],
          })}
          className="text-xs gap-1.5"
        >
          <ExternalLink className="h-3 w-3" />
          Open full answer
        </Button>
      </CardContent>
    </Card>
  )
}
